/** Everything the chat copy button drops: citation pills, their placeholders, table-cell chrome. */
export const CHAT_COPY_EXCLUDE_SELECTOR = '[data-copy-exclude]';

interface CopyEventLike {
  clipboardData: DataTransfer | null;
  preventDefault: () => void;
}

const CLOSING_PUNCTUATION = /^[.,;:!?)\]}]/;
const LEADING_SPACE = /^[ \t\u00a0]+/;
const TRAILING_SPACE = /[ \t\u00a0]+$/;

/** The elements a citation's spacing is tidied within: text in another one is not its neighbour. */
const BLOCK_SELECTOR = 'p, li, td, th, h1, h2, h3, h4, h5, h6, pre, blockquote, div';

/** Containers whose items the selection spans when one of them is the range's common ancestor. */
const ITEM_CONTAINER_TAGS = new Set(['UL', 'OL', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR']);

const blockOf = (node: Node): Element | null => node.parentElement?.closest(BLOCK_SELECTOR) ?? null;

/**
 * Removes every `selector` match under `root` and fixes the spacing around it, also across nodes
 * (Lexical wraps text runs in spans). Returns whether anything was removed.
 */
export const removeCitations = (root: Element | DocumentFragment, selector: string): boolean => {
  const citations = Array.from(root.querySelectorAll(selector));
  if (citations.length === 0) return false;

  const doc = root.ownerDocument ?? document;
  const gaps = new Set<Text>();
  citations.forEach((el) => {
    const gap = doc.createTextNode('');
    el.replaceWith(gap);
    gaps.add(gap);
  });

  const texts: Text[] = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) texts.push(node as Text);

  /** The nearest text in the same block, skipping other gaps and text already emptied. */
  const neighbour = (from: number, step: 1 | -1): Text | null => {
    const block = blockOf(texts[from]);
    for (let i = from + step; i >= 0 && i < texts.length; i += step) {
      if (blockOf(texts[i]) !== block) return null;
      if (!gaps.has(texts[i]) && texts[i].data) return texts[i];
    }
    return null;
  };

  texts.forEach((text, i) => {
    if (!gaps.has(text)) return;
    const before = neighbour(i, -1);
    const after = neighbour(i, 1);
    if (!after || CLOSING_PUNCTUATION.test(after.data)) {
      if (before) before.data = before.data.replace(TRAILING_SPACE, '');
    } else if (!before || TRAILING_SPACE.test(before.data)) {
      after.data = after.data.replace(LEADING_SPACE, '');
    }
  });

  gaps.forEach((gap) => gap.remove());
  return true;
};

/**
 * Clones the selection, including the surrounding list/table (so items aren't bare `<li>`/`<td>`)
 * and `<pre>` (to keep line breaks).
 */
const cloneSelection = (selection: Selection): HTMLDivElement => {
  const container = document.createElement('div');

  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    let content: Node = range.cloneContents();

    const ancestor = range.commonAncestorContainer;
    let el = ancestor instanceof Element ? ancestor : ancestor.parentElement;
    const pre = el?.closest('pre');
    while (el && ITEM_CONTAINER_TAGS.has(el.tagName)) {
      const shell = el.cloneNode(false);
      shell.appendChild(content);
      content = shell;
      el = el.parentElement;
    }
    if (pre) {
      const shell = pre.cloneNode(false);
      shell.appendChild(content);
      content = shell;
    }

    container.appendChild(content);
  }

  return container;
};

/**
 * Visible text of `container`, with line breaks. `innerText` needs a rendered element, so it's
 * mounted offscreen; jsdom has no `innerText`.
 */
const toPlainText = (container: HTMLElement): string => {
  container.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none';
  document.body.appendChild(container);
  const text = container.innerText ?? container.textContent ?? '';
  container.remove();
  return text;
};

const writeClipboard = (event: CopyEventLike, container: HTMLElement): boolean => {
  if (!event.clipboardData) return false;
  const text = toPlainText(container);
  event.preventDefault();
  event.clipboardData.setData('text/html', container.innerHTML);
  event.clipboardData.setData('text/plain', text);
  return true;
};

/**
 * `copy` handler: if the selection has `selector` matches, writes HTML and plain text without them.
 * Otherwise leaves it to the browser. Returns whether it wrote.
 */
export const copySelectionWithoutCitations = (event: CopyEventLike, selector: string): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;

  const container = cloneSelection(selection);
  if (!removeCitations(container, selector)) return false;
  return writeClipboard(event, container);
};

/**
 * Bubble-phase `copy` handler for an editor that already wrote `text/html`: rewrites HTML and plain
 * text without `selector` matches. Lexical's own format is kept, so pasting back keeps citations.
 */
export const rewriteCopiedHtmlWithoutCitations = (event: CopyEventLike, selector: string): boolean => {
  const html = event.clipboardData?.getData('text/html');
  if (!html) return false;

  const template = document.createElement('template');
  template.innerHTML = html;
  const container = document.createElement('div');
  container.appendChild(template.content);

  if (!removeCitations(container, selector)) return false;
  return writeClipboard(event, container);
};
